/**
 * @fileoverview Error Boundary global para captura de erros React.
 *
 * Comportamentos:
 * - Erros de módulo dinâmico (chunk stale após deploy): auto-recovery
 *   desregistra Service Workers e recarrega a página
 * - Outros erros: exibe card de erro com opções "Tentar novamente",
 *   "Recarregar página" e "Ir para Home"
 * - Em DEV: mostra stack trace expandível
 * - "Copiar detalhes" copia diagnóstico para suporte (URL, UA, mensagem,
 *   timestamp) — disponível em prod também
 * - Reporta erros ao Sentry via captureError
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home, Copy, Check, RotateCw } from 'lucide-react';
import { captureError } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isRecovering: boolean;
  retryCount: number;
  copied: boolean;
}

/** Silently unregister all service workers, then hard-reload the page */
function clearSwAndReload() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      const unregisterAll = registrations.map((r) => r.unregister());
      Promise.all(unregisterAll).finally(() => window.location.reload());
    }).catch(() => window.location.reload());
  } else {
    window.location.reload();
  }
}

/**
 * Stale-chunk detection. Match the patterns Vite, webpack, and Rollup throw
 * when the browser has cached an `index.html` that references chunk hashes
 * the server no longer has on disk (typically after a fresh deploy).
 * Broader matching = fewer "stuck on white screen" reports from users who
 * had a tab open during a deploy.
 */
function isStaleModuleError(error: Error) {
  const m = error.message || '';
  if (
    error instanceof TypeError &&
    (m.includes('Failed to fetch dynamically imported module') ||
     m.includes('error loading dynamically imported module'))
  ) return true;
  // webpack: ChunkLoadError; Vite occasionally surfaces "Loading chunk X failed"
  if (error.name === 'ChunkLoadError') return true;
  if (/Loading chunk \S+ failed/.test(m)) return true;
  if (/Importing a module script failed/.test(m)) return true;
  return false;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isRecovering: false, retryCount: 0, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureError(error, { componentStack: errorInfo.componentStack ?? undefined });

    // Auto-recover from stale service worker cache after a deploy.
    // This error happens when the browser has a cached old index.html that
    // references chunk filenames that no longer exist on the server.
    if (isStaleModuleError(error)) {
      this.setState({ isRecovering: true });
      clearSwAndReload();
    }
  }

  handleReset = () => {
    if (this.state.error && isStaleModuleError(this.state.error)) {
      this.setState({ isRecovering: true });
      clearSwAndReload();
    } else {
      this.setState((prev) => ({
        hasError: false,
        error: undefined,
        isRecovering: false,
        retryCount: prev.retryCount + 1,
        copied: false,
      }));
    }
  };

  /**
   * Builds a self-contained diagnostic blob for support tickets — URL,
   * timestamp, browser UA, retry count, and the raw error/stack. Available
   * in production too, not just dev.
   */
  handleCopy = async () => {
    if (!this.state.error) return;
    const lines = [
      `URL: ${typeof window !== 'undefined' ? window.location.href : 'n/a'}`,
      `Timestamp: ${new Date().toISOString()}`,
      `User-Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'}`,
      `Retries: ${this.state.retryCount}`,
      ``,
      `Error: ${this.state.error.toString()}`,
      this.state.error.stack ? `\nStack:\n${this.state.error.stack}` : '',
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1800);
    } catch {
      // Clipboard API blocked (insecure context, etc.) — silently no-op;
      // the user can still read the dev-only details panel.
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // If recovering from stale SW, show a loading message instead of the error
      if (this.state.isRecovering || (this.state.error && isStaleModuleError(this.state.error))) {
        return (
          <div className="flex items-center justify-center min-h-[50vh] p-6">
            <Card className="max-w-lg w-full bg-card border-border">
              <CardContent className="pt-6 text-center space-y-3">
                <RefreshCw className="h-8 w-8 animate-spin text-secondary mx-auto" />
                <p className="text-sm text-muted-foreground">
                  Atualizando o app... a página será recarregada automaticamente.
                </p>
              </CardContent>
            </Card>
          </div>
        );
      }

      const showHardReloadHint = this.state.retryCount >= 2;
      const brokenUrl = typeof window !== 'undefined' ? window.location.pathname : '';

      return (
        <div className="flex items-center justify-center min-h-[50vh] p-6">
          <Card className="max-w-lg w-full bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Algo deu errado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Ocorreu um erro ao carregar esta página. Por favor, tente novamente.
              </p>
              {brokenUrl && (
                <p className="text-[11px] text-muted-foreground/80 font-mono bg-muted/30 px-2 py-1 rounded">
                  Página: {brokenUrl}
                </p>
              )}
              {showHardReloadHint && (
                <p className="text-[11px] text-warning bg-warning/10 border border-warning/20 px-2 py-1.5 rounded">
                  Se o erro persistir, tente recarregar a página ou voltar para o Home.
                </p>
              )}
              {this.state.error && import.meta.env.DEV && (
                <details className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  <summary className="cursor-pointer font-medium mb-2">
                    Detalhes do erro (dev only)
                  </summary>
                  <code className="block whitespace-pre-wrap break-all">
                    {this.state.error.toString()}
                  </code>
                </details>
              )}
              <div className="flex flex-wrap gap-2">
                <Button onClick={this.handleReset} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Tentar novamente
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()} className="gap-2">
                  <RotateCw className="h-4 w-4" />
                  Recarregar página
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/'} className="gap-2">
                  <Home className="h-4 w-4" />
                  Ir para Home
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={this.handleCopy}
                  className="gap-2 text-xs text-muted-foreground hover:text-foreground ml-auto"
                  title="Copiar detalhes do erro para suporte"
                >
                  {this.state.copied ? (
                    <><Check className="h-3.5 w-3.5 text-success" /> Copiado</>
                  ) : (
                    <><Copy className="h-3.5 w-3.5" /> Copiar detalhes</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
